import { useEffect, useMemo, useState } from "react"

import { Col, Row } from "react-bootstrap"
import { BehaviorSubject, concat, of } from "rxjs"
import { catchError, distinctUntilChanged, map, switchMap } from "rxjs/operators"

import PendingClinicalTestRow from "./PendingClinicalTestRow"
import ApiResult from "../../../api/ApiResult"
import PendingClinicalTest from "../../../api/OrphanedResults/Models/PendingClinicalTest"
import PendingClinicalTestGridData from "../../../api/OrphanedResults/Models/PendingClinicalTestGridData"
import PendingClinicalTestOptions from "../../../api/OrphanedResults/Models/PendingClinicalTestOptions"
import { GetPendingClinicalTestsObservable } from "../../../api/OrphanedResults/ResultExceptionsProcess"
import handleAPIError from "../../../utils/handleAPIError"
import LoadingTableBodyWrapper from "../../LoadingTableBodyWrapper"
import LoadingTableWrapper from "../../LoadingTableWrapper"
import TablePagination from "../../TablePagination"

const PageSize = 5
interface PendingClinicalTestsProps {
  donorId: number
  selectedTest?: PendingClinicalTest
  showTitle?: boolean
  onSelectTest: (test: PendingClinicalTest | undefined) => void
}

function PendingClinicalTests({
  donorId,
  selectedTest = undefined,
  showTitle = true,
  onSelectTest,
}: PendingClinicalTestsProps) {
  const [gridData, setGridData] = useState<PendingClinicalTestGridData>({ Records: [], Pages: 0, Total: 0 })
  const [loadingTests, setLoadingTests] = useState<boolean>(false)

  const page$ = useMemo(
    () =>
      new BehaviorSubject<PendingClinicalTestOptions>({
        DonorID: donorId,
        PageNumber: 1,
        PageSize,
      }),
    [donorId]
  )

  useEffect(() => {
    const subscription = page$
      .pipe(
        distinctUntilChanged(),
        switchMap((params) =>
          concat(
            of({ loading: true }),
            GetPendingClinicalTestsObservable(params).pipe(
              map((result) => ({ loading: false, result })),
              catchError((error) => of({ loading: false, result: undefined, error }))
            )
          )
        )
      )
      .subscribe(
        ({ loading, result, error }: ApiResult<PendingClinicalTestGridData>) => {
          setLoadingTests(loading)

          if (!loading) {
            const parsedRecords = result?.Records.map((test) => ({
              ...test,
              DateOrdered: test.DateOrdered ? new Date(test.DateOrdered) : undefined,
            }))
            setGridData({
              ...result,
              Records: parsedRecords || [],
              Pages: result?.Pages ?? 0,
              Total: result?.Total ?? 0,
            })
          }

          handleAPIError(error)
        },
        (error) => handleAPIError(error, () => setLoadingTests(false))
      )
    return () => subscription.unsubscribe()
  }, [page$])

  const handleSelectTest = (testId: string | undefined) => {
    const selected = gridData.Records.find((t) => t.TestDetailClinicalID === testId)
    onSelectTest(selected?.TestDetailClinicalID === selectedTest?.TestDetailClinicalID ? undefined : selected)
  }

  const handleChangePage = (page: number) => {
    handleSelectTest(undefined)
    page$.next({ ...page$.value, PageNumber: page })
  }

  return (
    <>
      {showTitle && <h5 className="text-body">Pending Clinical Tests</h5>}
      <LoadingTableWrapper loading={loadingTests} variant="light" withHeader>
        <table className="table table-bordered">
          <thead className="thead-light">
            <tr>
              <th className="text-center col-1" aria-label="..." />
              <th className="text-nowrap">Authorization ID</th>
              <th className="text-nowrap">Reason</th>
              <th className="text-nowrap">Type</th>
              <th className="text-nowrap col-2">Created</th>
            </tr>
          </thead>
          <tbody className="bg-light">
            <LoadingTableBodyWrapper
              loading={loadingTests}
              recordsCount={gridData.Records.length}
              columnsCount={6}
              noDataText="No Tests Found"
            >
              {gridData.Records.map((test: PendingClinicalTest) => (
                <PendingClinicalTestRow
                  key={`pct_${test.TestDetailClinicalID}`}
                  test={test}
                  highlighted={false}
                  hideCheckbox={false}
                  selected={selectedTest?.TestDetailClinicalID === test.TestDetailClinicalID}
                  onSelect={handleSelectTest}
                />
              ))}
            </LoadingTableBodyWrapper>
          </tbody>
        </table>
      </LoadingTableWrapper>
      {gridData.Records.length > 0 && (
        <Row>
          <Col sm={2}>Total records: {gridData.Total}</Col>
          <Col>
            <TablePagination
              pageNumber={page$.value.PageNumber}
              pages={gridData.Pages}
              onChange={handleChangePage}
              className="mb-0"
            />
          </Col>
        </Row>
      )}
    </>
  )
}

export default PendingClinicalTests
